import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { Editor as TinymceEditor } from 'tinymce';

import type { Service } from 'webapp-types';
import { AppEvent } from 'webapp/enums/events';
import { isAlive } from 'webapp/utils/common';

import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import willDestroy from 'webapp/modifiers/will-destroy';
import BlockSettingsFontSizeComponent from 'webapp/components/block/settings/font-size';
import FormCheckboxSwitchComponent from 'webapp/components/form/checkbox-switch';
import FormHtmlEditorFullComponent from 'webapp/components/form/html-editor-full';

import type Block from 'webapp/models-custom/block';
import type VisualizationText from 'webapp/models-custom/visualization/text';

interface Args {
	blockM: Block;
}

export default class BlockSettingsTextComponent extends Component<Args> {
	@service('event-manager') declare eventManagerService: Service.EventManager;

	get block(): Block {
		return this.args.blockM;
	}

	get blockSetup() {
		return this.block.blockSetup;
	}

	get visualization(): VisualizationText {
		return this.blockSetup.visualization as VisualizationText;
	}

	// `@blockM` is already null when TinyMCE's deferred blur/change lands after the right panel closes
	get shownText(): string {
		return this.args.blockM ? this.visualization.shownText : '';
	}

	get showTimestamp(): boolean {
		return !this.block.omitTimestamp;
	}

	// temporary hidden
	get isTimestampSwitchShown(): boolean {
		return false;
	}

	@action
	changeShowTimestamp(showTimestamp: boolean): void {
		const block = this.block;
		block.omitTimestamp = !showTimestamp;
		block.blockSetup.saveBlock();
	}

	@action
	onChange(text: string): void {
		if (!this.args.blockM) {
			return;
		}

		const cleanText = text.trim(),
			currentSavedText = this.block.text;

		if (cleanText !== currentSavedText) {
			this.block.text = cleanText;
			this.blockSetup.saveBlock();
		}
	}

	editor?: TinymceEditor; // set from this.visualization.getEditorOptionsForComponent

	get editorOptions() {
		return this.visualization.getEditorOptionsForComponent(this);
	}

	@action
	setupEditedEvent() {
		this.updateEditorOpenForId();

		this.eventManagerService.on(AppEvent.VIS_TEXT_EDITED, this, (eventData) => {
			if (isAlive(this) && eventData.id === this.block?.id) {
				const editor = this.editor;
				if (editor) {
					const currentContent = editor.getContent();
					if (currentContent !== eventData.content) {
						editor.setContent(eventData.content);
					}
				}
			}
		});
	}

	@action
	teardownEditedEvent() {
		this.eventManagerService.off(AppEvent.VIS_TEXT_EDITED, this);
	}

	// ---- rerendering editor when switching blocks

	private editorOpenForId = '';

	@action
	onOutsideModelChange() {
		if (this.editorOpenForId === this.block.id) {
			return;
		}

		this.updateEditorOpenForId();
		this.editor?.setContent(this.visualization.shownText);
	}

	private updateEditorOpenForId() {
		this.editorOpenForId = this.block.id!;
	}
	<template>
		<div
			class="row row--mce user-html"
			{{didInsert this.setupEditedEvent}}
			{{didUpdate this.onOutsideModelChange @blockM}}
			{{willDestroy this.teardownEditedEvent}}
		>
			<div id="b{{@blockM.id}}" class="block">
				<FormHtmlEditorFullComponent
					@text={{this.shownText}}
					@onChange={{this.onChange}}
					@options={{this.editorOptions}}
					@static={{true}}
				/>
			</div>
		</div>

		{{#if this.isTimestampSwitchShown}}
			<div class="row row--toggle">
				<FormCheckboxSwitchComponent
					@name="showTimestamp"
					@selected={{this.showTimestamp}}
					@onChange={{this.changeShowTimestamp}}
				/>
				<label>Show update time</label>
			</div>
		{{/if}}

		<BlockSettingsFontSizeComponent @blockM={{@blockM}} />
	</template>
}
